import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import * as turfProjection from "@turf/projection";
import * as turf from "@turf/turf";

// Components
import {
  Filters,
  Header,
  Tabs,
  Pagination,
  Table,
  GridCard,
} from "Components/Observations";

// Hooks
import { ObservationsTable } from "hooks/useTable";
// Store Utils
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { TableProps } from "Components/Programs/Table/types";
import { filtersInitialState } from "store/filters/initialState";
import { IfetchObservations } from "store/observations/initialState";
import { fetchObservationsAction } from "store/observations";
import { TailSpin } from "react-loader-spinner";
import NoRecordFound from "view/components/NoRecordFound";
import { useLocation, useParams } from "react-router-dom";
import {
  resetObservationsDirectoryOrderAction,
  updateColumnsVisibilityOfObservations,
  updateColumnsVisibilityOfObservationsByStatus,
} from "store/directoriesOrders/reducer.actions";
import usePermissions from "hooks/usePermissions";
import { observationsResetFiltersAction } from "store/filters";
import { saveFiltersDataToLocalStorage } from "utils/cacheOrRetriveFiltersDate";
import { setObservationsFiltersAction } from "store/filters/reducer.actions";
import useCustomBranding from "hooks/useCustomBranding";

export const Observations = () => {
  const [showInput, setShowInput] = useState(false);
  const { communityId, programId } = useParams();

  const inputRef = useRef<HTMLDivElement>(null);
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();

  useLayoutEffect(() => {
    dispatch(setObservationsFiltersAction());
  }, []);
  const { data } = useSelector<RootState, IfetchObservations>(
    (state) => state?.allObservations
  );
  const { observationsFilter } = useSelector<RootState, filtersInitialState>(
    (state) => state.Filters
  );
  const { observations } = usePermissions();

  const location = useLocation();
  const state = location.state;

  const {
    flexRender,
    columnVisibility,
    columnOrder,
    columnsConfig,
    table,
    setColumnsConfig,
    setColumnOrder,
    setColumnVisibility,
    setSorting,
    sorting,
  } = ObservationsTable(data?.observations);

  useEffect(() => {
    if (programId || communityId) {
      dispatch(updateColumnsVisibilityOfObservations(programId || communityId));
    }
    dispatch(fetchObservationsAction(communityId, programId, state));
    saveFiltersDataToLocalStorage(observationsFilter);
  }, [observationsFilter, programId]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        observationsFilter.searchString === ""
      ) {
        setShowInput(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [observationsFilter.searchString]);

  // useEffect(() => {
  //   return () => {
  //     dispatch(observationsResetFiltersAction());
  //   };
  // }, []);

  return (
    <div className="flex flex-col items-start justify-start w-full pt-4 pb-20">
      <div className="flex flex-col items-start justify-start flex-grow-0 flex-shrink-0 w-full gap-3">
        <Header />
        <div className="flex flex-col items-start self-stretch justify-start flex-grow-0 flex-shrink-0 w-full gap-4">
          <div className="flex flex-col items-start justify-start flex-grow-0 flex-shrink-0 w-full">
            <div className="flex flex-col items-start self-stretch justify-start flex-grow-0 flex-shrink-0 gap-4">
              <div className="flex justify-start  flex-wrap items-center self-stretch flex-grow-0 flex-shrink-0 min-h-[60px]">
                <Tabs />
              </div>
            </div>
          </div>
          <div className="flex flex-col items-start self-stretch justify-start flex-grow-0 flex-shrink-0">
            <div className="flex flex-col items-start self-stretch justify-start flex-grow-0 flex-shrink-0 gap-2">
              <Filters
                columnOrder={columnOrder}
                setColumnOrder={setColumnOrder}
                columns={columnsConfig}
                columnVisibility={columnVisibility}
                setColumnVisibility={setColumnVisibility}
                setColumnsConfig={setColumnsConfig}
              />
            </div>
          </div>
        </div>

        <RednderView
          flexRender={flexRender}
          table={table}
          columnOrder={columnOrder}
          columns={columnsConfig}
          columnVisibility={columnVisibility}
          setSorting={setSorting}
          sorting={sorting}
        />
      </div>
    </div>
  );
};
export default Observations;

const RednderView: React.FC<TableProps> = ({
  flexRender,
  table,
  columnOrder,
  columns,
  columnVisibility,
  setSorting,
  sorting,
}) => {
  const { data, stateIs } = useSelector<RootState, IfetchObservations>(
    (state) => state?.allObservations
  );

  const { observationsFilter } = useSelector<RootState, filtersInitialState>(
    (state) => state?.Filters
  );

  const { primaryColor } = useCustomBranding();

  if (observationsFilter.viewType === "grid") {
    return (
      <div className="flex flex-col items-start self-stretch justify-start flex-grow-0 flex-shrink-0 w-full">
        {stateIs === "Idle" && data?.observations?.length === 0 ? (
          <NoRecordFound />
        ) : stateIs === "Pending" ? (
          <div className="loader min-h-[45vh] justify-center items-center flex w-full">
            <TailSpin
              height="50"
              width="50"
              color={primaryColor}
              ariaLabel="tail-spin-loading"
              radius="2"
              wrapperStyle={{}}
              wrapperClass="tailspin-loader"
              visible={true}
            />
          </div>
        ) : (
          <GridCard />
        )}
        <div
          className={`bg-bgWhite dark:bg-secondaryLight  w-full ${stateIs === "Idle" && data?.observations?.length === 0 ? "" : "mt-2"
            }`}
        >
          <Pagination />
        </div>
      </div>
    );
  } else if (observationsFilter.viewType === "list") {
    return (
      <div className="flex flex-col items-start self-stretch justify-start flex-grow-0 flex-shrink-0 w-full dark:bg-secondaryLight bg-bgWhite">
        {stateIs == "Idle" && data?.observations?.length === 0 ? (
          <NoRecordFound />
        ) : stateIs === "Pending" ? (
          <div className="loader min-h-[45vh] justify-center items-center flex w-full">
            <TailSpin
              height="50"
              width="50"
              color={primaryColor}
              ariaLabel="tail-spin-loading"
              radius="2"
              wrapperStyle={{}}
              wrapperClass="tailspin-loader"
              visible={true}
            />
          </div>
        ) : (
          <Table
            flexRender={flexRender}
            table={table}
            columnOrder={columnOrder}
            columns={columns}
            columnVisibility={columnVisibility}
            setSorting={setSorting}
            sorting={sorting}
          />
        )}
        <Pagination />
      </div>
    );
  } else {
    return null;
  }
};
